import { Link, useParams } from "react-router-dom";
import { ArrowRight, Clock, ChevronRight } from "lucide-react";
import {
  getPostsByCategory,
  getCategoryName,
  formatDate,
  categorySlug,
  getCategoryDescription,
} from "./posts";
import { useMetaTags } from "../components/MetaTags";

export function BlogCategory() {
  const { category } = useParams<{ category: string }>();
  const categoryName = category ? getCategoryName(category) : undefined;
  const posts = category ? getPostsByCategory(category) : [];
  const categoryDescription = categoryName
    ? getCategoryDescription(categoryName)
    : "";

  useMetaTags({
    title: categoryName
      ? `${categoryName} - OmniCanvas Blog`
      : "Category Not Found - OmniCanvas Blog",
    description: categoryName
      ? // The per-category line carries the distinct wording; the count adds
        // the only other useful fact. The long shared tail that used to follow
        // pushed every category past Google's ~160 char cut and read as the
        // same duplicated boilerplate on all sixteen pages.
        `${categoryDescription} Browse ${posts.length} ${categoryName} article${posts.length === 1 ? "" : "s"} on the OmniCanvas blog.`
      : "Sorry, this category does not exist on the OmniCanvas blog. Explore guides on spatial notetaking, visual thinking, and infinite canvas workflows.",
    url: category
      ? `https://omnicanvasnotes.com/blog/${category}`
      : "https://omnicanvasnotes.com/blog",
  });

  if (!categoryName) {
    return (
      <div className="min-h-screen bg-white pt-32">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h1 className="text-3xl font-bold text-slate-900 mb-4">
            Category not found
          </h1>
          <Link
            to="/blog"
            className="text-indigo-600 hover:text-indigo-700 font-medium"
          >
            Back to blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <section className="pt-32 pb-20">
        <div className="max-w-4xl mx-auto px-6">
          <nav className="flex items-center gap-1.5 text-sm text-slate-500 mb-8">
            <Link to="/blog" className="hover:text-slate-700 transition-colors">
              Blog
            </Link>
            <ChevronRight size={14} />
            <span className="text-slate-900 font-medium">{categoryName}</span>
          </nav>

          <img
            src={`https://pub-530c8de906034aa3846ea946c751a5aa.r2.dev/category-${category}.png`}
            alt={categoryName}
            className="w-full h-48 object-cover rounded-xl mb-8"
            loading="eager"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />

          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-slate-900 mb-4">
            {categoryName}
          </h1>
          <p className="text-lg text-slate-600 mb-12">
            {posts.length} article{posts.length !== 1 ? "s" : ""}
          </p>

          <div className="space-y-10">
            {posts.map((post) => (
              <article
                key={post.slug}
                className="group border-b border-slate-100 pb-10 last:border-0"
              >
                <div className="flex gap-6">
                  {post.coverImage !== null && (
                    <Link to={`/blog/${categorySlug(post.category)}/${post.slug}`} className="shrink-0">
                      <img
                        src={post.coverImage ?? `https://pub-530c8de906034aa3846ea946c751a5aa.r2.dev/${post.slug}.png`}
                        alt={post.title}
                        className="w-48 h-28 object-cover rounded-lg"
                        loading="lazy"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                    </Link>
                  )}
                  <div className="min-w-0">
                    <div className="flex items-center gap-3 text-sm text-slate-500 mb-3">
                      <span>{formatDate(post.date)}</span>
                      <span className="flex items-center gap-1">
                        <Clock size={13} />
                        {post.readTime}
                      </span>
                    </div>
                    <Link to={`/blog/${categorySlug(post.category)}/${post.slug}`}>
                      <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-2 group-hover:text-indigo-600 transition-colors">
                        {post.title}
                      </h2>
                    </Link>
                    <p className="text-slate-600 mb-4 leading-relaxed line-clamp-2">
                      {post.description}
                    </p>
                    <Link
                      to={`/blog/${categorySlug(post.category)}/${post.slug}`}
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-indigo-600 hover:text-indigo-700 transition-colors"
                    >
                      Read more
                      <ArrowRight
                        size={14}
                        className="group-hover:translate-x-0.5 transition-transform"
                      />
                    </Link>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
